/**
 * Central config for @repo/observability. Every app calls `initObservability`
 * once at startup — client-side in a root layout provider, server-side in a
 * request-scoped context — passing its app name and defaults.
 */

export const FEATURES = [
  'auth',
  'booking',
  'payment',
  'scheduling',
  'messaging',
  'admin',
  'support',
  'onboarding',
  'intake',
  'profile',
  'sessions',
  'supervision',
  'dashboard',
  'clients',
  'progress-notes',
  'assessments',
  'referrals',
  'consent',
  'brand',
  'unknown',
] as const

export type Feature = (typeof FEATURES)[number]

export type UserImpact = 'blocked' | 'degraded' | 'silent' | 'unknown'

export type Severity = 'debug' | 'info' | 'warn' | 'error' | 'critical'

export type AppName =
  | 'therapist'
  | 'client'
  | 'imwell'
  | 'imwell-admin'
  | 'imwell-clinician'
  | 'between-sessions'
  | 'broker'
  | 'employee'
  | 'employer'
  | 'resolve'
  | 'admin'
  | 'server'

export interface ObservabilityConfig {
  app: AppName
  release?: string
  environment?: string
  /**
   * Default user-role tag applied to every client-side insert unless the
   * app calls `clientLogger.setUser({userRole})` with a more specific
   * value. Apps with a fixed role (imwell → 'student', imwell-admin →
   * 'school-admin', client → 'client') can set this once at
   * <ObservabilityProvider>.
   */
  defaultUserRole?: string
  /**
   * Fingerprint prefixes to drop at source (never inserted). Use for
   * known-noise categories that don't warrant a DB row.
   * Example: ['Error::Login failed', 'Error::AbortError'].
   */
  mutedFingerprintPrefixes?: string[]
}

let currentConfig: ObservabilityConfig | null = null

export function initObservability(config: ObservabilityConfig): void {
  currentConfig = {
    ...config,
    environment: config.environment || process.env.VERCEL_ENV || process.env.NODE_ENV || 'production',
    release: config.release || process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || process.env.VERCEL_GIT_COMMIT_SHA,
  }
}

export function getConfig(): ObservabilityConfig {
  if (!currentConfig) {
    // Fallback so a caller that forgot to init doesn't blow up the app.
    return { app: 'server', environment: process.env.NODE_ENV || 'production' }
  }
  return currentConfig
}

export function isMuted(fingerprint: string): boolean {
  const cfg = getConfig()
  const prefixes = cfg.mutedFingerprintPrefixes || []
  return prefixes.some((p) => fingerprint.startsWith(p))
}
